import { useTranslation } from '../../i18n';
import { localName } from '../../utils/format';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Button, Checkbox, Dropdown, Form, Grid, Input, Modal, Select, Space, Spin, Switch, Tabs, Tag, Typography, message,
} from 'antd';
import {
    CodeOutlined, DeleteOutlined, DesktopOutlined, DownOutlined, EllipsisOutlined, PauseCircleOutlined,
    PlayCircleOutlined, PoweroffOutlined, ReloadOutlined, SwapOutlined, SyncOutlined, ThunderboltOutlined,
    UndoOutlined, CheckCircleOutlined, ExportOutlined, MedicineBoxOutlined,
} from '@ant-design/icons';
import PageHeader from '../PageHeader';
import StatusTag from '../StatusTag';
import { VncConsole, TerminalConsole } from '../console';
import { userPath, adminPath } from '../../config/paths';
import OverviewTab from './vmdetail/OverviewTab';
import MonitorTab from './vmdetail/MonitorTab';
import DiskTab from './vmdetail/DiskTab';
import NetworkTab from './vmdetail/NetworkTab';
import SecurityGroupTab from './vmdetail/SecurityGroupTab';
import SnapshotBackupTab from './vmdetail/SnapshotBackupTab';
import SettingsTab from './vmdetail/SettingsTab';
import UpgradeTab from './vmdetail/UpgradeTab';
import QuotaTab from './vmdetail/QuotaTab';
import VdsPinTab from './vmdetail/VdsPinTab';
import SshKeysTab from './vmdetail/SshKeysTab';
import SshKeyPicker, { EMPTY_SSH_PICK, sshPickPayload, sshPickValid } from './SshKeyPicker';
import { checkPublicKeys } from '../../utils/sshKeygen';
import { OPERABLE, generatePassword, makeAdapter, ok, runAction } from './vmdetail/utils';

const { Text } = Typography;

/** Normalises the libvirt / list power state to running | shutoff | paused | null (unknown). */
const normPower = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'running') return 'running';
    if (v === 'shutoff' || v === 'shutdown' || v === 'stopped') return 'shutoff';
    if (v === 'paused' || v === 'suspended') return 'paused';
    return null;
};

const POWER_NAMES = {
    boot: 'Power on', shutdown: 'Shut down', reboot: 'Restart', destroy: 'Force shutdown', reset: 'Force restart',
};

const withAuth = (data) => {
    let wsUrl = data.ws_url;
    if (data.token && data.ts) wsUrl += `${wsUrl.includes('?') ? '&' : '?'}token=${data.token}&ts=${data.ts}`;
    return wsUrl;
};

/**
 * Shared cloud-server detail page for both consoles.
 *   <VmDetailView mode="user" api={evmService} />
 *   <VmDetailView mode="admin" api={evmAdminService} />
 */
const VmDetailView = ({ mode = 'user', api, renderExtra }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const isAdmin = mode === 'admin';
    const adapter = useMemo(() => makeAdapter(mode, api), [mode, api]);
    const listPath = isAdmin ? adminPath('/evm/vms') : userPath('/evm/vms');

    const [loading, setLoading] = useState(true);
    const [vm, setVm] = useState(null);
    const [row, setRow] = useState(null);
    const [vmState, setVmState] = useState(null);
    const [acting, setActing] = useState(false);
    const [tab, setTab] = useState('overview');

    const [network, setNetwork] = useState(null);
    const [networkLoading, setNetworkLoading] = useState(false);
    const networkLoaded = useRef(false);

    // modals
    const [rebuildOpen, setRebuildOpen] = useState(false);
    const [images, setImages] = useState([]);
    const [migrateOpen, setMigrateOpen] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [vncData, setVncData] = useState(null);
    const [terminalData, setTerminalData] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [rebuildForm] = Form.useForm();
    const [migrateForm] = Form.useForm();
    const [exportOpen, setExportOpen] = useState(false);
    const [rescueLogin, setRescueLogin] = useState(null);
    const [exportForm] = Form.useForm();
    const exportStop = Form.useWatch('stop', exportForm);

    // ---------- loaders ----------
    const loadRow = useCallback(async (v) => {
        if (!v) return;
        try {
            const r = await adapter.getListRow(v);
            if (r) setRow(r);
        } catch (e) { /* enrichment is best-effort */ }
    }, [adapter]);

    const loadVm = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const res = await adapter.getDetail(id);
            if (ok(res) && res.data?.vm) {
                setVm(res.data.vm);
                if (res.data.row) setRow(res.data.row);
                else loadRow(res.data.vm);
            } else if (!silent) {
                message.error(res?.msg || res?.error || t('Failed to load'));
            }
        } catch (e) {
            if (!silent) message.error(t('Failed to load'));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [adapter, id, loadRow, t]);

    const loadState = useCallback(async () => {
        try {
            const res = await adapter.getState(id);
            if (ok(res)) setVmState(res.data);
        } catch (e) { /* ignore */ }
    }, [adapter, id]);

    const loadNetwork = useCallback(async () => {
        setNetworkLoading(true);
        try {
            const res = await api.getVmNetwork(id);
            if (ok(res)) setNetwork(res.data);
            else {
                setNetwork(null);
                message.error(res?.msg || res?.error || t('Failed to load network information'));
            }
        } finally {
            setNetworkLoading(false);
        }
    }, [api, id, t]);

    const reload = useCallback(() => {
        loadVm({ silent: true });
        loadState();
    }, [loadVm, loadState]);

    useEffect(() => {
        loadVm();
        loadState();
        const t = setInterval(loadState, 10000);
        return () => clearInterval(t);
    }, [loadVm, loadState]);

    // While the VM is being created / rebuilt / migrated (or a task is running), refresh fast until it settles.
    const transitional = ['create', 'rebuild', 'migrate'].includes(vm?.status) || !!row?.busy;
    useEffect(() => {
        if (!transitional) return undefined;
        const t = setInterval(() => { loadVm({ silent: true }); loadState(); }, 5000);
        return () => clearInterval(t);
    }, [transitional, loadVm, loadState]);

    // keep busy / traffic fresh
    useEffect(() => {
        if (!vm) return undefined;
        const t = setInterval(() => loadRow(vm), 30000);
        return () => clearInterval(t);
    }, [vm, loadRow]);

    useEffect(() => {
        if ((tab === 'network' || tab === 'security') && !networkLoaded.current) {
            networkLoaded.current = true;
            loadNetwork();
        }
    }, [tab, loadNetwork]);

    // ---------- derived ----------
    const status = vm?.status;
    const busy = !!(row?.busy ?? vm?.busy);
    const power = normPower(row?.power_state) || normPower(vmState?.state);
    const operable = OPERABLE.includes(status);
    const powerDisabled = busy || acting || (!operable && !isAdmin);
    const tabDisabled = busy || !!vm?.rescue_at || (!isAdmin && !operable);
    const primaryIp = row?.ip || vm?.ip_address || (vm?.ips || []).find((i) => i.is_main)?.address || (vm?.ips || [])[0]?.address;

    const canBoot = power !== 'running';
    const canStop = power !== 'shutoff';

    // ---------- actions ----------
    const afterAction = () => {
        setTimeout(() => { loadState(); loadVm({ silent: true }); }, 2000);
        setTimeout(() => { loadState(); loadVm({ silent: true }); }, 8000);
    };

    const doPower = async (action) => {
        setActing(true);
        const res = await runAction(() => api.power(id, action), t('{{value}} submitted', { value: t(POWER_NAMES[action]) }), t('{{value}} failed', { value: t(POWER_NAMES[action]) }));
        setActing(false);
        if (res) afterAction();
    };

    const confirmPower = (action) => {
        if (action === 'boot') { doPower(action); return; }
        const force = action === 'destroy' || action === 'reset';
        Modal.confirm({
            title: t('Confirm {{value}}?', { value: t(POWER_NAMES[action]) }),
            content: force ? t('Unsaved data may be lost.') : undefined,
            okText: t(POWER_NAMES[action]),
            okButtonProps: { danger: force },
            cancelText: t('Cancel'),
            onOk: () => doPower(action),
        });
    };

    // Full stop + start: a guest reboot would keep the old definition.
    const coldRestart = () => Modal.confirm({
        title: t('Restart?'),
        okText: t('Restart'),
        cancelText: t('Cancel'),
        onOk: async () => {
            setActing(true);
            const res = await runAction(() => adapter.coldRestart(id), t('Restart submitted'), t('Restart failed'));
            setActing(false);
            if (res) afterAction();
        },
    });

    const openRebuild = async () => {
        rebuildForm.setFieldsValue({ image_id: vm.image?.id, passwd: generatePassword(12), ssh_replace: false, ssh_pick: EMPTY_SSH_PICK });
        setRebuildOpen(true);
        setImages(await adapter.getImages(vm));
    };

    const submitRebuild = (values) => {
        Modal.confirm({
            title: t('Reinstall the operating system?'),
            content: t('The system disk will be erased. Data disks will not be affected.'),
            okText: t('Reinstall'),
            okButtonProps: { danger: true },
            cancelText: t('Cancel'),
            onOk: async () => {
                setSubmitting(true);
                const data = { image_id: values.image_id };
                if (values.passwd) data.passwd = values.passwd;
                const img = images.find((i) => i.id === values.image_id);
                if (!isAdmin && values.ssh_replace && img?.type !== 'windows') Object.assign(data, sshPickPayload(values.ssh_pick));
                const res = await runAction(() => api.rebuild(id, data), t('Reinstallation task submitted'), t('Failed to reinstall OS'));
                setSubmitting(false);
                if (res) { setRebuildOpen(false); afterAction(); }
            },
        });
    };

    const inRescue = !!vm?.rescue_at;
    const enterRescue = () => Modal.confirm({
        title: t('Boot into rescue mode?'),
        content: t('The VM restarts into a rescue system; your disks keep their names (/dev/vda …)'),
        okText: t('Rescue mode'),
        cancelText: t('Cancel'),
        onOk: async () => {
            setActing(true);
            const res = await runAction(() => api.vmRescue(id, 'enter'), t('Rescue mode submitted'), t('Failed to enter rescue mode'));
            setActing(false);
            if (res) { setRescueLogin({ user: res.data?.user || 'root', password: res.data?.password || '', ip: primaryIp }); afterAction(); }
        },
    });
    const exitRescue = () => Modal.confirm({
        title: t('Exit rescue mode?'),
        content: t('The VM restarts from its own system disk.'),
        okText: t('Exit rescue mode'),
        cancelText: t('Cancel'),
        onOk: async () => {
            setActing(true);
            const res = await runAction(() => api.vmRescue(id, 'exit'), t('Exit rescue mode submitted'), t('Failed to exit rescue mode'));
            setActing(false);
            if (res) afterAction();
        },
    });

    const openVnc = async () => {
        const res = await api.getNoVnc(id);
        if (ok(res) && res.data?.ws_url) {
            setVncData({ wsUrl: withAuth(res.data), password: res.data.password || vm.vnc_passwd || '' });
        } else {
            message.error(res?.msg || res?.error || t('Failed to establish VNC connection'));
        }
    };

    const openTerminal = async () => {
        const res = await api.getTerminal(id);
        if (ok(res) && res.data?.ws_url) setTerminalData({ wsUrl: withAuth(res.data) });
        else message.error(res?.msg || res?.error || t('Failed to establish terminal connection'));
    };

    // admin-only
    const adminConfirm = (title, content, fn, okText, success, fail, { danger = true, then } = {}) => Modal.confirm({
        title,
        content,
        okText,
        okButtonProps: { danger },
        cancelText: t('Cancel'),
        onOk: async () => {
            const res = await runAction(fn, success, fail);
            if (res) (then || afterAction)();
        },
    });

    const suspend = () => adminConfirm(t('Pause this cloud server?'), t('Users will be unable to use it.'), () => api.suspendVm(id), t('Suspended'), t('Paused'), t('Failed to pause'));
    const unsuspend = () => adminConfirm(t('Restore this cloud server?'), undefined, () => api.unsuspendVm(id), t('Restore'), t('Restored'), t('Restore failed'), { danger: false });
    const resetFlow = () => adminConfirm(t('Reset this month\'s traffic?'), undefined, () => api.resetFlow(id), t('Reset'), t('Traffic reset'), t('Failed to reset traffic'), { danger: false, then: () => loadVm({ silent: true }) });
    const deleteVm = () => adminConfirm(
        t('Delete {{value}}?', { value: vm.hostname || vm.name }),
        t('The instance and its disks will be permanently deleted.'),
        () => api.deleteVm(id),
        t('Delete'),
        t('Deletion task submitted'),
        t('Deletion failed'),
        { then: () => navigate(listPath) },
    );
    const openMigrate = async () => {
        migrateForm.resetFields();
        setMigrateOpen(true);
        setNodes(await adapter.getNodes());
    };
    const submitMigrate = async ({ node_id: nodeId }) => {
        setSubmitting(true);
        const res = await runAction(() => api.migrateVm(id, nodeId), t('Migration task submitted'), t('Migration failed'));
        setSubmitting(false);
        if (res) { setMigrateOpen(false); afterAction(); }
    };

    const isWindows = /win/i.test(`${vm?.image?.type || ''} ${vm?.os_type || ''} ${vm?.image?.os_family || ''}`);
    const openExport = () => {
        const d = new Date();
        const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
        exportForm.setFieldsValue({ name: `${vm.hostname || vm.name}-${stamp}`, generalize: !isWindows, stop: false });
        setExportOpen(true);
    };
    const submitExport = async (values) => {
        setSubmitting(true);
        const res = await runAction(
            () => api.exportVmImage(id, { name: values.name.trim(), generalize: !!values.generalize, stop: !!values.stop }),
            t('Export task submitted'),
            t('Export failed'),
        );
        setSubmitting(false);
        if (res) {
            setExportOpen(false);
            if (res.data?.warning) message.warning(res.data.warning);
            afterAction();
        }
    };

    // ---------- render ----------
    if (loading) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;

    if (!vm) {
        return (
            <Alert
                type="error"
                showIcon
                message={t('Cloud server does not exist')}
                action={<Button onClick={() => navigate(listPath)}>{t('Back to list')}</Button>}
            />
        );
    }

    const moreItems = [
        { key: 'destroy', label: t('Force shutdown'), icon: <PoweroffOutlined />, danger: true, disabled: powerDisabled || !canStop },
        { key: 'reset', label: t('Force restart'), icon: <ThunderboltOutlined />, danger: true, disabled: powerDisabled || !canStop },
        { key: 'rebuild', label: t('Reinstall OS'), icon: <ReloadOutlined />, disabled: tabDisabled || inRescue },
        inRescue
            ? { key: 'rescue_exit', label: t('Exit rescue mode'), icon: <MedicineBoxOutlined />, disabled: busy || acting }
            : { key: 'rescue', label: t('Rescue mode'), icon: <MedicineBoxOutlined />, disabled: busy || acting || status !== 'active' },
        ...(isAdmin ? [
            { type: 'divider' },
            status === 'suspend'
                ? { key: 'unsuspend', label: t('Restore service'), icon: <CheckCircleOutlined />, disabled: busy }
                : { key: 'suspend', label: t('Pause service'), icon: <PauseCircleOutlined />, disabled: busy },
            { key: 'migrate', label: t('Migrate'), icon: <SwapOutlined />, disabled: busy },
            { key: 'resetflow', label: t('Reset traffic'), icon: <UndoOutlined /> },
            { key: 'export', label: t('Export image'), icon: <ExportOutlined />, disabled: busy },
            { type: 'divider' },
            { key: 'delete', label: t('Delete'), icon: <DeleteOutlined />, danger: true, disabled: busy },
        ] : []),
    ];

    const powerItems = [
        canBoot && { key: 'boot', label: t('Power on'), icon: <PlayCircleOutlined />, disabled: powerDisabled },
        canStop && { key: 'shutdown', label: t('Shut down'), icon: <PoweroffOutlined />, disabled: powerDisabled },
        canStop && { key: 'reboot', label: t('Restart'), icon: <SyncOutlined />, disabled: powerDisabled },
    ].filter(Boolean);

    const consoleItems = [
        { key: 'vnc', label: 'VNC', icon: <DesktopOutlined />, disabled: busy },
        { key: 'terminal', label: t('Terminal'), icon: <CodeOutlined />, disabled: busy },
    ];

    const onMenu = ({ key }) => {
        if (POWER_NAMES[key]) return confirmPower(key);
        const map = {
            rebuild: openRebuild,
            rescue: enterRescue,
            rescue_exit: exitRescue,
            suspend,
            unsuspend,
            migrate: openMigrate,
            resetflow: resetFlow,
            ...(isAdmin ? { export: openExport } : {}),
            delete: deleteVm,
            vnc: openVnc,
            terminal: openTerminal,
        };
        return map[key]?.();
    };

    const extra = isMobile ? (
        <Dropdown
            trigger={['click']}
            menu={{
                onClick: onMenu,
                items: [...powerItems, { type: 'divider' }, ...consoleItems, { type: 'divider' }, ...moreItems],
            }}
        >
            <Button loading={acting}>{t('Actions ')}<DownOutlined /></Button>
        </Dropdown>
    ) : (
        <>
            {powerItems.map((p) => (
                <Button
                    key={p.key}
                    icon={p.icon}
                    disabled={p.disabled}
                    type={p.key === 'boot' ? 'primary' : 'default'}
                    onClick={() => confirmPower(p.key)}
                >
                    {p.label}
                </Button>
            ))}
            <Dropdown trigger={['click']} menu={{ onClick: onMenu, items: consoleItems }}>
                <Button icon={<DesktopOutlined />} disabled={busy}>{t('Remote connection ')}<DownOutlined /></Button>
            </Dropdown>
            <Dropdown trigger={['click']} menu={{ onClick: onMenu, items: moreItems }}>
                <Button icon={<EllipsisOutlined />} aria-label={t('More actions')} />
            </Dropdown>
        </>
    );

    const subtitle = (
        <Space size={12} wrap split={<Text type="secondary">·</Text>}>
            {primaryIp && <Text copyable={{ text: primaryIp }} style={{ fontSize: 13 }}>{primaryIp}</Text>}
            {row?.region && <span>{row.region}</span>}
            {(row?.plan?.name || (typeof row?.plan === 'string' && row.plan)) && <span>{localName(row.plan?.name || row.plan)}</span>}
            {isAdmin && vm.node?.name && <span>{vm.node.name}</span>}
        </Space>
    );

    const tags = (
        <Space size={4} wrap>
            <StatusTag status={status} domain="evm" />
            {power && <StatusTag status={power} domain="evm" />}
            {row?.kind === 'vds' && <Tag color="purple" style={{ marginInlineEnd: 0 }}>VDS</Tag>}
            {inRescue && <Tag icon={<MedicineBoxOutlined />} color="orange" style={{ marginInlineEnd: 0 }}>{t('Rescue mode')}</Tag>}
            {busy && <Tag icon={<SyncOutlined spin />} color="processing" style={{ marginInlineEnd: 0 }}>{t('Task in progress')}</Tag>}
        </Space>
    );

    const common = { id, vm, api, adapter, mode, isMobile, disabled: tabDisabled, reload };

    const tabItems = [
        { key: 'overview', label: t('Overview'), children: <OverviewTab vm={vm} row={row} mode={mode} isMobile={isMobile} id={id} api={api} active={tab === 'overview'} onMore={() => setTab('monitor')} onChanged={reload} /> },
        { key: 'monitor', label: t('Monitoring'), children: <MonitorTab id={id} api={api} isMobile={isMobile} active={tab === 'monitor'} /> },
        {
            key: 'disk',
            label: t('Disk'),
            children: <DiskTab {...common} onInvoice={(inv) => navigate(userPath(`/billing/invoices/${inv}`))} />,
        },
        {
            key: 'network',
            label: t('Network'),
            children: <NetworkTab {...common} network={network} loading={networkLoading} reloadNetwork={loadNetwork} />,
        },
        {
            key: 'security',
            label: t('Security groups'),
            children: <SecurityGroupTab id={id} api={api} network={network} loading={networkLoading} reloadNetwork={loadNetwork} isMobile={isMobile} disabled={tabDisabled} />,
        },
        ...(!isWindows ? [{
            key: 'sshkeys',
            label: t('SSH Keys'),
            children: <SshKeysTab id={id} api={api} mode={mode} active={tab === 'sshkeys'} disabled={tabDisabled} />,
        }] : []),
        ...(!isAdmin ? [{
            key: 'upgrade',
            label: t('Upgrade'),
            children: <UpgradeTab {...common} active={tab === 'upgrade'} onInvoice={(inv) => navigate(userPath(`/billing/invoices/${inv}`))} />,
        }] : []),
        { key: 'snapshot', label: t('Snapshots & backups'), children: <SnapshotBackupTab {...common} active={tab === 'snapshot'} onInvoice={(inv) => navigate(userPath(`/billing/invoices/${inv}`))} /> },
        { key: 'settings', label: t('Settings'), children: <SettingsTab {...common} openRebuild={openRebuild} /> },
        ...(isAdmin && row?.kind === 'vds' && api.getVmVds ? [{ key: 'vds', label: t('CPU pinning'), children: <VdsPinTab id={id} api={api} active={tab === 'vds'} /> }] : []),
        ...(isAdmin ? [{ key: 'quota', label: t('Quotas & limits'), children: <QuotaTab id={id} api={api} isMobile={isMobile} active={tab === 'quota'} onChanged={reload} /> }] : []),
    ];

    return (
        <div>
            <PageHeader
                title={vm.hostname || vm.name}
                tags={tags}
                subtitle={subtitle}
                onBack={() => navigate(listPath)}
                extra={renderExtra ? <>{extra}{renderExtra({ id, vm, status })}</> : extra}
                dense={isMobile}
            />

            {!operable && !busy && status && (
                <Alert
                    style={{ marginBottom: 12 }}
                    type={status === 'error' ? 'error' : 'warning'}
                    showIcon
                    message={<>{t('Current status:')}<StatusTag status={status} domain="evm" />{!isAdmin && t(', currently unavailable')}</>}
                />
            )}

            {row?.restriction && (
                <Alert
                    style={{ marginBottom: 12 }}
                    type="warning"
                    showIcon
                    message={row.restriction.state === 'public_paused' ? t('Public network suspended') : t('Instance restricted. Please contact support.')}
                    description={row.restriction.reason || undefined}
                />
            )}

            {inRescue && (
                <Alert
                    style={{ marginBottom: 12 }}
                    type="warning"
                    showIcon
                    message={t('The VM is running the rescue system')}
                    action={(
                        <Button size="small" loading={acting} disabled={busy} onClick={exitRescue}>
                            {t('Exit rescue mode')}
                        </Button>
                    )}
                />
            )}

            {vm.pending_restart && !busy && (
                <Alert
                    style={{ marginBottom: 12 }}
                    type="info"
                    showIcon
                    message={t('Configuration updated; takes effect after a restart')}
                    action={(
                        <Button size="small" type="primary" loading={acting} disabled={!operable && !isAdmin} onClick={coldRestart}>
                            {t('Restart')}
                        </Button>
                    )}
                />
            )}

            <Tabs activeKey={tab} onChange={setTab} items={tabItems} size={isMobile ? 'small' : 'middle'} />

            <Modal
                title={t('Rescue login')}
                open={!!rescueLogin}
                onCancel={() => setRescueLogin(null)}
                onOk={() => setRescueLogin(null)}
                cancelButtonProps={{ style: { display: 'none' } }}
                okText={t('Done')}
                destroyOnClose
            >
                {rescueLogin && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Alert type="info" showIcon message={t('Shown only once. Your SSH keys also work.')} />
                        {rescueLogin.ip && <Text>{t('Address')}: <Text code copyable>{rescueLogin.ip}</Text></Text>}
                        <Text>{t('Username')}: <Text code copyable>{rescueLogin.user}</Text></Text>
                        <Text>{t('Password')}: <Text code copyable>{rescueLogin.password}</Text></Text>
                    </Space>
                )}
            </Modal>

            {/* Rebuild */}
            <Modal
                title={t('Reinstall OS')}
                open={rebuildOpen}
                onCancel={() => setRebuildOpen(false)}
                onOk={() => rebuildForm.submit()}
                okText={t('Reinstall')}
                okButtonProps={{ danger: true }}
                confirmLoading={submitting}
                destroyOnClose
            >
                <Form form={rebuildForm} layout="vertical" onFinish={submitRebuild}>
                    <Form.Item name="image_id" label={t('Image')} rules={[{ required: true, message: t('Select an image') }]}>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder={t('Select an image')}
                            options={images.map((img) => ({
                                value: img.id,
                                disabled: !!img.unavailable,
                                label: img.unavailable ? `${img.name} · ${t(img.unavailable)}` : img.name,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(a, b) => a.image_id !== b.image_id}>
                        {({ getFieldValue }) => (images.find((i) => i.id === getFieldValue('image_id'))?.license_notice
                            ? <div style={{ margin: '-16px 0 16px' }}><Text type="warning" style={{ fontSize: 12 }}>{t('Windows requires your own license activation (evaluation: 180 days)')}</Text></div>
                            : null)}
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(a, b) => a.image_id !== b.image_id}>
                        {({ getFieldValue }) => (
                            <Form.Item name="passwd" label={t('Password')} tooltip={t('Leave blank to keep the current password')} style={{ marginBottom: 0 }}>
                                <Input
                                    addonBefore={images.find((i) => i.id === getFieldValue('image_id'))?.username || 'root'}
                                    addonAfter={<a onClick={() => rebuildForm.setFieldsValue({ passwd: generatePassword(12) })}>{t('Random')}</a>}
                                />
                            </Form.Item>
                        )}
                    </Form.Item>
                    {!isAdmin && (
                        <Form.Item noStyle shouldUpdate={(a, b) => a.image_id !== b.image_id || a.ssh_replace !== b.ssh_replace}>
                            {({ getFieldValue }) => (images.find((i) => i.id === getFieldValue('image_id'))?.type === 'windows' ? null : (
                                <>
                                    <Form.Item name="ssh_replace" valuePropName="checked" style={{ margin: '16px 0 8px' }}>
                                        <Checkbox>{t('Replace SSH keys')}</Checkbox>
                                    </Form.Item>
                                    {getFieldValue('ssh_replace') ? (
                                        <Form.Item
                                            name="ssh_pick"
                                            style={{ marginBottom: 0 }}
                                            rules={[{
                                                validator: (_, v) => {
                                                    const paste = checkPublicKeys(v?.paste);
                                                    if (paste === 'private' || paste === 'invalid') return Promise.reject(new Error(t('Invalid public key')));
                                                    if (v?.key_only && !sshPickValid(v)) return Promise.reject(new Error(t('Select or paste a public key')));
                                                    return Promise.resolve();
                                                },
                                            }]}
                                        >
                                            <SshKeyPicker />
                                        </Form.Item>
                                    ) : <Text type="secondary" style={{ fontSize: 12 }}>{t('Keep current keys')}</Text>}
                                </>
                            ))}
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            {/* Migrate (admin) */}
            {isAdmin && (
                <Modal
                    title={t('Migrate')}
                    open={migrateOpen}
                    onCancel={() => setMigrateOpen(false)}
                    onOk={() => migrateForm.submit()}
                    confirmLoading={submitting}
                    destroyOnClose
                >
                    <Form form={migrateForm} layout="vertical" onFinish={submitMigrate}>
                        <Form.Item name="node_id" label={t('Target node')} rules={[{ required: true, message: t('Select a node') }]} style={{ marginBottom: 0 }}>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                placeholder={t('Select a node')}
                                options={nodes.filter((n) => n.id !== vm.node?.id).map((n) => ({
                                    value: n.id,
                                    label: `${n.name}${n.ipaddress ? ` · ${n.ipaddress}` : ''}`,
                                }))}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            )}

            {/* Export image (admin) */}
            {isAdmin && (
                <Modal
                    title={t('Export image')}
                    open={exportOpen}
                    onCancel={() => setExportOpen(false)}
                    onOk={() => exportForm.submit()}
                    okText={t('Export')}
                    okButtonProps={{ disabled: power !== 'shutoff' && !exportStop }}
                    confirmLoading={submitting}
                    destroyOnClose
                >
                    <Form form={exportForm} layout="vertical" onFinish={submitExport}>
                        <Form.Item name="name" label={t('Image name')} rules={[{ required: true, whitespace: true, message: t('Please enter a name') }]}>
                            <Input maxLength={64} />
                        </Form.Item>
                        <Form.Item
                            name="generalize"
                            label={t('Generalize')}
                            tooltip={t('Clears the machine ID, SSH host keys, logs, etc.')}
                            valuePropName="checked"
                            extra={isWindows ? <Text type="warning">{t('Windows images must be sysprepped inside the guest before they can be generalized')}</Text> : undefined}
                            style={{ marginBottom: power === 'shutoff' ? 0 : undefined }}
                        >
                            <Switch disabled={isWindows} />
                        </Form.Item>
                        {power !== 'shutoff' && (
                            <Form.Item name="stop" valuePropName="checked" style={{ marginBottom: 0 }}>
                                <Checkbox>{t('Shut down and export')}</Checkbox>
                            </Form.Item>
                        )}
                    </Form>
                </Modal>
            )}

            {/* Consoles */}
            {vncData && (
                <VncConsole
                    title={`${vm.hostname || vm.name} · ${vm.login_user || 'root'}`}
                    wsUrl={vncData.wsUrl}
                    password={vncData.password}
                    onReconnect={openVnc}
                    onClose={() => setVncData(null)}
                    onError={(msg) => message.error(msg)}
                />
            )}
            <Modal
                title={t('Terminal · {{value}}', { value: vm.hostname || vm.name })}
                open={!!terminalData}
                onCancel={() => setTerminalData(null)}
                footer={null}
                width={isMobile ? '100%' : 1024}
                style={{ top: 20 }}
                destroyOnClose
            >
                {terminalData && (
                    <TerminalConsole
                        wsUrl={terminalData.wsUrl}
                        onReconnect={openTerminal}
                        style={{ height: isMobile ? 360 : 500 }}
                        onError={(msg) => message.error(msg)}
                    />
                )}
            </Modal>
        </div>
    );
};

export default VmDetailView;
